<!-- #region HTML -->
<template>
  <div class="app">
    <!-- #region Alert Component -->
    <alert :SystemErrorAlert="isSystemError" :codeSent="codeSent" @update:SystemErrorAlert="isSystemError = $event"
      @update:codeSent="codeSent = $event">
    </alert>
    <!-- #endregion -->

    <!-- #region Main Container -->
    <div class="main-container form-container">
      <!-- #region Steps -->
      <div class="steps">
        <!-- #region Back Button -->
        <div class="back-bg" @click="goBack">
          <img class="back" src="@/assets/img/back.svg" alt="戻る" width="100%" />
        </div>
        <!-- #endregion -->

        <!-- #region Right Box -->
        <div class="right-box">
          <!-- #region Sub Container -->
          <div class="sub-container">
            <!-- #region Title -->
            <div class="title">
              <label>パスワード再設定</label>
            </div>
            <!-- #endregion -->

            <!-- #region Step Box -->
            <div class="step-box text-left">
              <label class="step-title sp-hide">ステップ 2/2</label>
              <label class="step-description sp-hide">メールに記載された認証コードを入力し、パスワードの再設定を完了してください。</label>
            </div>
            <!-- #endregion -->
          </div>
          <!-- #endregion -->

          <!-- #region Step Description Box -->
          <div class="step-description-box text-left">
            <label class="step-title">ステップ 2/2</label>
            <div class="step-description">メールに記載された認証コードを入力し、パスワードの再設定を完了してください。</div>
          </div>
          <!-- #endregion -->
        </div>
        <!-- #endregion -->
      </div>
      <!-- #endregion -->

      <!-- #region Form -->
      <form novalidate @submit.prevent="validateUser">
        <!-- #region Card -->
        <md-card class="form-container">
          <!-- #region Form Fields -->
          <div class="md-form">
            <hr>
            <!-- #region Email Field -->
            <div class="text-left display-fixed-element">
              <label for="email" class="fixed-variables-title">メールアドレスID：</label>
              <span id="email">{{ form.email }}</span>
            </div>
            <!-- #endregion -->

            <!-- #region Verification Code Field -->
            <md-field :class="getValidationClass('verificationCode')">
              <md-input class="mb-20" id="verification-code" v-model="form.verificationCode" name="verification-code"
                placeholder="認証コード" :disabled="sending" @input="clearErrorFlag"/>
              <span v-if="isInvalidCode === true" class="md-error error">認証コードが誤っています。</span>
              <span v-else-if="!$v.form.verificationCode.required" class="md-error error">入力してください。</span>
              <span v-else-if="!$v.form.verificationCode.minLength" class="md-error error">認証コードが誤っています。</span>
              <span v-else-if="!$v.form.verificationCode.maxLength" class="md-error error">認証コードが誤っています。</span>
            </md-field>
            <!-- #endregion -->

            <!-- #region Resend Code Link -->
            <div class="resend-code verify-code">
              <a :class="{ disabled: isResendCode }" @click="resendCode">認証コード再送信</a>
            </div>
            <!-- #endregion -->
          </div>
          <!-- #endregion -->

          <!-- #region Password Field -->
          <md-field :class="getValidationClass('password')">
            <md-input ref="password" id="password" v-model="form.password" name="password" :type="passwordInputType"
              placeholder="パスワード" :disabled="sending" :class="passwordStrengthClass" @input="checkPasswordStrength" />
            <div class="password-strength-bar">
              <span v-for="n in 5" :key="n" :class="getSegmentClass(n)" class="strength-segment"></span>
            </div>
            <div class="password-strength" :class="passwordStrengthClass">
              <span class="" v-if="passwordStrengthMessage">{{ passwordStrengthMessage }}</span>
            </div>
            <span class="toggle-password" @click="togglePassword()">
              <img :src="passwordInputType === 'text' ? eyeOpenImg : eyeCloseImg" alt="パスワードを表示" width="100%" />
            </span>
            <span v-if="!$v.form.password.required" class="md-error error mb-10">パスワードを入力してください。</span>
            <span v-else-if="!$v.form.password.minLength" class="md-error error mb-10">8文字以上、64文字以下で設定してください。</span>
            <span v-else-if="!$v.form.password.maxLength" class="md-error error mb-10">8文字以上、64文字以下で設定してください。</span>
            <span v-else-if="showPasswordLengthError" class="md-error error mb-10">8文字以上、64文字以下で設定してください。</span>
          </md-field>
          <!-- #endregion -->

          <!-- #region Password Alert -->
          <div class="pw-alert" :class="passwordStrengthClass"
            v-if="form.password.length > 7 && form.password.length < 65 && (isPasswordAcceptable === true || isPasswordWeak === true)">
            <div class="alert-sp text-left">
              <div class="pw-warning" v-if="passwordWarning">{{ passwordWarning }}</div>
              <div v-if="passwordSuggestions.length > 0">
                <div class="text-left">
                  <ul class="password-suggestions">
                    <li v-for="(suggestion, index) in passwordSuggestions" :key="index"> {{ suggestion }} </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <!-- #endregion -->

          <!-- #region Password Confirm Field -->
          <div class="md-form">
            <md-field :class="getValidationClass('passwordConfirm')">
              <md-input id="password-confirm" v-model="form.passwordConfirm" name="password-confirm"
                :type="passwordConfirmInputType" placeholder="パスワード (確認)" :disabled="sending" @focus="onPasswordConfirmFocus" />
              <span class="toggle-password" @click="togglePasswordConfirm()">
                <img :src="passwordConfirmInputType === 'text' ? eyeOpenImg : eyeCloseImg" alt="パスワード(確認)を表示"
                  width="100%" />
              </span>
              <span v-if="!$v.form.passwordConfirm.required" class="md-error error">パスワード(確認)を入力してください。</span>
              <span v-else-if="!$v.form.passwordConfirm.minLength"
                class="md-error error">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="!$v.form.passwordConfirm.maxLength"
                class="md-error error">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="!$v.form.passwordConfirm.sameAsPassword"
                class="md-error error">パスワードとパスワード(確認)が、異なっています。</span>
              <span v-else-if="isUserNotActive === true"
                class="md-error error alias-error">このメールアドレスは既に使用されています。他のメールアドレスでご登録ください。</span>
            </md-field>
          </div>
          <!-- #endregion -->

          <!-- #region Progress Bar -->
          <md-progress-bar v-if="sending" md-mode="indeterminate" />
          <!-- #endregion -->

          <!-- #region Submit Container -->
          <div class="submit-container">
            <md-card-actions>
              <md-button type="submit" class="primary-button" :disabled="sending || isPasswordWeak || !isFormValid">パスワードを再設定する</md-button>
            </md-card-actions>
          </div>
          <!-- #endregion -->
        </md-card>
        <!-- #endregion -->
      </form>
      <!-- #endregion -->
    </div>
    <!-- #endregion -->
  </div>
</template>

<!-- #endregion -->

<!-- #region JavaScript -->
<script>
// @ is an alias to /src
import Alert from '@/layout/Alerts.vue';
import { togglePassword, togglePasswordConfirm, isFormValid } from '@/utils/base.js';
import { checkPasswordStrength, getSegmentClass } from '@/utils/passwordUtils';
import { Auth } from 'aws-amplify';
import { validationMixin } from 'vuelidate';
import { maxLength, minLength, required, sameAs } from 'vuelidate/lib/validators';

const includesAlphabetsAndNumbers = (value) => {
  const pattern = /^(?=.*?[a-z])(?=.*?\d)[a-z\d]{8,64}$/i
  return pattern.test(value)
}

export default {
  name: 'VerifyForgotPassword',
  mixins: [validationMixin],
  components: {
    "alert": Alert
  },
  data: () => ({
    form: {
      email: null,
      verificationCode: null,
      password: '',
      passwordConfirm: null,
    },
    userSaved: false,
    codeSent: false,
    sending: false,
    lastUser: null,
    message: '',
    isUserNotActive: false,
    isSystemError: false,
    isInvalidCode: false,
    isInvalidpassword: false,
    isResendCode: false,
    isPasswordWeak: false,
    isPasswordAcceptable: false,
    passwordVisible: false,
    passwordConfirmVisible: false,
    eyeOpenImg: require('@/assets/img/eye-open.svg'),
    eyeCloseImg: require('@/assets/img/eye-close.svg'),
    passwordStrengthClass: '',
    passwordStrengthMessage: '',
    passwordWarning: '',
    passwordSuggestions: [],
    passwordStrengthScore: 0,
    passwordInputType: 'password',
    passwordConfirmInputType: 'password',
    hasError: false,
    showPasswordLengthError: false,
  }),
  async created() {
    this.form.email = this.$store.state.forgotPasswordEmail
  },
  validations: {
    form: {
      verificationCode: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(6),
      },
      password: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
      },
      passwordConfirm: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
        sameAsPassword: sameAs('password'),
      },
    },
  },
  computed: {
    isFormValid() {
      return isFormValid(this.$v);
    },
  },
  methods: {
    goBack() {
      window.history.back();
    },
    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName]
      if (field) {
        if (fieldName == 'verificationCode') {
          if (field.$anyError) {
            this.isInvalidCode = false
          }
        }
        if (fieldName === 'password' && this.showPasswordLengthError) {
          return { 'md-invalid': true };
        }
        return {
          'md-invalid': field.$invalid && field.$dirty || this.isInvalidCode,
        }
      }
      if (field) {
        if (fieldName == 'password') {
          if (field.$anyError) {
            this.isInvalidPassword = false
          }
        }
        return {
          'md-invalid': field.$invalid && field.$dirty,
        }
      }
    },
    clearForm() {
      this.$v.$reset()
      this.form.verificationCode = null
      this.form.password = null
      this.form.passwordConfirm = null
    },
    clearErrorFlag() {
      this.isInvalidCode = false
      this.isUserNotActive = false
      this.isSystemError = false
    },
    async saveUser() {
      this.clearErrorFlag()
      this.sending = true
      try {
        const verifyResult = await Auth.forgotPasswordSubmit(
          this.$store.state.forgotPasswordEmail,
          this.form.verificationCode,
          this.form.password
        )
        this.$store.commit('clearAuthData')
        this.$router.push({ name: 'completeForgotPassword' })
      } catch (error) {
        if (error.code === 'CodeMismatchException') {
          this.isInvalidCode = true
        } else {
          this.isSystemError = true
          window.scrollTo(0, 0);
        }
        this.sending = false
        return;
      }
      this.userSaved = true
      this.sending = false
      this.clearForm()
    },
    async validateUser() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.checkPasswordStrength();
      if (this.isPasswordWeak) {
        this.isPasswordWeak = true;
        this.isSystemError = false;
        window.scrollTo(0, 0);
        return;
      } else if (this.isPasswordAcceptable) {
        this.isPasswordAcceptable = true;
        this.isPasswordWeak = false;
      }
      this.isPasswordWeak = false;

      this.saveUser()
    },
    togglePassword() {
      this.passwordInputType = togglePassword(this.passwordInputType);
    },
    togglePasswordConfirm() {
      this.passwordConfirmInputType = togglePasswordConfirm(this.passwordConfirmInputType);
    },
    checkPasswordStrength() {
      const passwordStrength = checkPasswordStrength(this.form.password);
      this.passwordStrengthClass = passwordStrength.class;
      this.passwordStrengthMessage = passwordStrength.message;
      this.passwordWarning = passwordStrength.warning;
      this.passwordSuggestions = passwordStrength.suggestions;
      this.passwordStrengthScore = passwordStrength.score;
      this.isPasswordWeak = passwordStrength.isPasswordWeak;
      this.isPasswordAcceptable = passwordStrength.isPasswordAcceptable;

      this.showPasswordLengthError = false;
    },
    getSegmentClass(index) {
      return getSegmentClass(index, this.form.password.length, this.passwordStrengthScore);
    },
    async resendCode() {
      this.clearErrorFlag();
      this.isResendCode = true;
      this.sending = true;

      setTimeout(async () => {
        try {
          const result = await Auth.forgotPassword(this.$store.state.forgotPasswordEmail);

          this.codeSent = true;
          setTimeout(() => {
            this.codeSent = false;
          }, 5000);

        } catch (error) {
          if (error.code === 'UserNotFoundException') {
          } else if (error.code === 'InvalidParameterException') {
            this.isUserNotActive = true;
          } else {
            this.isSystemError = true;
            window.scrollTo(0, 0);
          }
          this.hasError = true;
          this.sending = false;
          return;
        }

        this.isResendCode = false;
        this.sending = false;
      }, 1500);

      return false;
    },
    onPasswordConfirmFocus() {
      this.showPasswordLengthError = !this.$v.form.password.minLength || !this.$v.form.password.maxLength;
    },
  },
}
</script>
<!-- #endregion -->